// src/KitchenHelperApp.js
import React, { useState, useEffect, useMemo } from 'react';

export default function KitchenHelperApp() {
  const [tab, setTab] = useState('timer'); // 'timer' | 'convert' | 'subs'

  return (
    <div style={{ minHeight: '100vh', padding: '1.5rem', background: '#f5f5f5' }}>
      <div
        style={{
          maxWidth: 960,
          margin: '0 auto',
          background: '#ffffff',
          borderRadius: 16,
          padding: '1.5rem',
          boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
        }}
      >
        <header style={{ marginBottom: '1.5rem' }}>
          <h1 style={{ margin: 0, fontSize: '1.8rem', fontWeight: 700 }}>
            Cooking Habits – Kitchen Helper
          </h1>
          <p style={{ margin: '0.25rem 0', color: '#666' }}>
            Timer · Conversions · Ingredient Substitutions
          </p>
        </header>

        {/* Tabs */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginBottom: '1.5rem',
            borderRadius: 999,
            background: '#f0f0f0',
            padding: 4,
          }}
        >
          {[
            { id: 'timer', label: 'Timer' },
            { id: 'convert', label: 'Convert' },
            { id: 'subs', label: 'Substitutions' },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              style={{
                flex: 1,
                padding: '0.6rem 0.8rem',
                borderRadius: 999,
                border: 'none',
                cursor: 'pointer',
                fontWeight: 600,
                background: tab === t.id ? '#111111' : 'transparent',
                color: tab === t.id ? '#ffffff' : '#333333',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Panels */}
        {tab === 'timer' && <TimerPanel />}
        {tab === 'convert' && <ConversionPanel />}
        {tab === 'subs' && <SubstitutionsPanel />}
      </div>
    </div>
  );
}

/* ---------------------- TIMER PANEL ---------------------- */

function TimerPanel() {
  const DEFAULT_MINUTES = '0';
  const DEFAULT_SECONDS = '30';
  const DEFAULT_LABEL = '';

  const [minutes, setMinutes] = useState(DEFAULT_MINUTES);
  const [seconds, setSeconds] = useState(DEFAULT_SECONDS);
  const [label, setLabel] = useState(DEFAULT_LABEL);

  const [remaining, setRemaining] = useState(
    toInt(DEFAULT_MINUTES) * 60 + toInt(DEFAULT_SECONDS)
  );
  const [running, setRunning] = useState(false);
  const [doneMessage, setDoneMessage] = useState(''); // ⬅️ NEW

  // total seconds based on current inputs
  const total = useMemo(() => {
    const m = toInt(minutes);
    const s = toInt(seconds);
    const raw = m * 60 + s;
    return Math.max(0, raw || 0);
  }, [minutes, seconds]);

  // when minutes/seconds change and timer is NOT running,
  // sync remaining to the new total
  useEffect(() => {
    if (!running) {
      setRemaining(total);
    }
  }, [minutes, seconds, total]);

  // ticking logic: only changes remaining if running=true
  useEffect(() => {
    if (!running) return;

    const id = setInterval(() => {
      setRemaining(prev => {
        if (prev <= 1) {
          clearInterval(id);
          setRunning(false);
          handleTimeUp(label || 'Timer'); // ⬅️ call helper
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(id);
  }, [running, label]);

  function handleTimeUp(title) {
    // no more window.alert – just set UI message
    setDoneMessage(`Your "${title}" is ready!`);
  }

  function handleStart() {
    // if we've hit 0 and there is a valid total, restart from total
    if (remaining <= 0 && total > 0) {
      setRemaining(total);
    }
    if (total > 0) {
      setDoneMessage('');   // ⬅️ clear any old "ready" message
      setRunning(true);
    }
  }

  function handlePause() {
    setRunning(false);      // just stop ticking, don't reset remaining
  }

  function handleResetAll() {
    // full form reset back to defaults
    setRunning(false);
    setMinutes(DEFAULT_MINUTES);
    setSeconds(DEFAULT_SECONDS);
    setLabel(DEFAULT_LABEL);
    const base = toInt(DEFAULT_MINUTES) * 60 + toInt(DEFAULT_SECONDS);
    setRemaining(base);
    setDoneMessage('');     // ⬅️ clear message
  }

  function handleApplyFromInputs() {
    // "Set" current minutes/seconds as the new starting point
    setRunning(false);
    setRemaining(total);
    setDoneMessage('');     // ⬅️ clear message
  }

  function handlePreset(preset) {
    setRunning(false);
    setMinutes(String(Math.floor(preset.seconds / 60)));
    setSeconds(String(preset.seconds % 60));
    setLabel(preset.label);
    setRemaining(preset.seconds);
    setDoneMessage('');     // ⬅️ clear message
  }

  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');

  const presets = [
    { label: 'Eggs', seconds: 7 * 60 },
    { label: 'Tea', seconds: 3 * 60 },
    { label: 'Pasta', seconds: 10 * 60 },
    { label: 'Pizza', seconds: 12 * 60 },
    { label: 'Rest', seconds: 15 * 60 },
  ];

  return (
    <div style={{ display: 'grid', gap: '1.25rem' }}>
      <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700 }}>Kitchen Timer</h2>

      {/* Label + Inputs */}
      <div
        style={{
          display: 'grid',
          gap: '0.75rem',
          gridTemplateColumns: '2fr 1fr 1fr auto',
          alignItems: 'end',
        }}
      >
        <LabeledInput
          label="Label"
          value={label}
          onChange={e => setLabel(e.target.value)}
          placeholder="What are we timing?"
        />
        <LabeledInput
          label="Minutes"
          type="number"
          min="0"
          max="999"
          value={minutes}
          onChange={e => setMinutes(e.target.value)}
        />
        <LabeledInput
          label="Seconds"
          type="number"
          min="0"
          max="59"
          value={seconds}
          onChange={e => setSeconds(e.target.value)}
        />
        <button
          type="button"
          onClick={handleApplyFromInputs}
          style={{
            padding: '0.5rem 0.9rem',
            borderRadius: 10,
            border: '1px solid #ddd',
            background: '#ffffff',
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          Set
        </button>
      </div>

      {/* Time display */}
      <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
        <div
          style={{
            fontSize: '3.5rem',
            fontWeight: 800,
            letterSpacing: '0.05em',
          }}
        >
          {mm}:{ss}
        </div>
        <div style={{ marginTop: 4, color: '#666' }}>{label || 'Timer'}</div>

        {doneMessage && (
          <div
            style={{
              marginTop: 8,
              color: '#b0413e',
              fontWeight: 600,
              fontSize: '0.95rem',
            }}
          >
            {doneMessage}
          </div>
        )}
      </div>

      {/* Controls */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 12,
        }}
      >
        <button
          type="button"
          onClick={running ? handlePause : handleStart}
          style={{
            padding: '0.9rem',
            borderRadius: 12,
            border: 'none',
            background: '#111111',
            color: '#ffffff',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {running ? 'Pause' : 'Start'}
        </button>
        <button
          type="button"
          onClick={handleResetAll}
          style={{
            padding: '0.9rem',
            borderRadius: 12,
            border: '1px solid #ddd',
            background: '#ffffff',
            color: '#333',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Reset
        </button>
      </div>

      {/* Presets */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
        {presets.map(p => (
          <button
            key={p.label}
            type="button"
            onClick={() => handlePreset(p)}
            style={{
              padding: '0.35rem 0.8rem',
              borderRadius: 999,
              border: 'none',
              background: '#f1f1f1',
              fontSize: '0.85rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {p.label} {Math.round(p.seconds / 60)}m
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------------------- CONVERSIONS PANEL ---------------------- */

function ConversionPanel() {
  const [category, setCategory] = useState('volume'); // 'volume' | 'weight' | 'temperature'
  const [fromUnit, setFromUnit] = useState('cup');
  const [toUnit, setToUnit] = useState('tbsp');
  const [value, setValue] = useState('1');

  const units = {
    volume: ['tsp', 'tbsp', 'fl_oz', 'cup', 'pt', 'qt', 'gal', 'ml', 'l'],
    weight: ['g', 'kg', 'oz', 'lb'],
    temperature: ['C', 'F', 'K'],
  };

  const convert = useMemo(() => createConverter(), []);

  useEffect(() => {
    if (category === 'volume') {
      setFromUnit('cup');
      setToUnit('tbsp');
    } else if (category === 'weight') {
      setFromUnit('g');
      setToUnit('oz');
    } else {
      setFromUnit('C');
      setToUnit('F');
    }
  }, [category]);

  const result = useMemo(() => {
    try {
      const n = Number(value);
      if (Number.isNaN(n)) return '--';
      return String(convert(category, fromUnit, toUnit, n));
    } catch {
      return '--';
    }
  }, [category, fromUnit, toUnit, value, convert]);

  return (
    <div style={{ display: 'grid', gap: '1.25rem' }}>
      <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700 }}>Measurement Conversions</h2>

      {/* Category buttons */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {['volume', 'weight', 'temperature'].map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setCategory(c)}
            style={{
              padding: '0.4rem 0.9rem',
              borderRadius: 999,
              border: 'none',
              background: category === c ? '#111111' : '#f0f0f0',
              color: category === c ? '#ffffff' : '#333',
              fontWeight: 600,
              textTransform: 'capitalize',
              cursor: 'pointer',
            }}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Units + value */}
      <div
        style={{
          display: 'grid',
          gap: '0.75rem',
          gridTemplateColumns: '1fr 1fr 1fr',
        }}
      >
        <UnitPicker
          label="From"
          value={fromUnit}
          options={units[category]}
          onChange={setFromUnit}
        />
        <UnitPicker label="To" value={toUnit} options={units[category]} onChange={setToUnit} />
        <LabeledInput
          label="Value"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          type="number"
        />
      </div>

      {/* Result */}
      <div
        style={{
          borderRadius: 12,
          border: '1px solid #eee',
          padding: '0.9rem 1rem',
          background: '#fafafa',
        }}
      >
        <div style={{ fontSize: '0.85rem', color: '#666' }}>Result</div>
        <div style={{ fontSize: '2rem', fontWeight: 800 }}>{result}</div>
      </div>

      <p style={{ margin: 0, fontSize: '0.85rem', color: '#777' }}>
        Note: Volume ↔ weight conversions depend on ingredient density. This tool converts within
        volume or within weight. Use substitutions for ingredient-specific guidance.
      </p>
    </div>
  );
}

/* ---------------------- SUBSTITUTIONS PANEL ---------------------- */

function SubstitutionsPanel() {
  const [query, setQuery] = useState('');
  const data = useMemo(() => substitutionsData(), []);
  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data;
    return data.filter(
      (i) =>
        i.ingredient.toLowerCase().includes(q) ||
        (i.aliases || []).some((a) => a.toLowerCase().includes(q))
    );
  }, [query, data]);

  return (
    <div style={{ display: 'grid', gap: '1.25rem' }}>
      <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700 }}>Ingredient Substitutions</h2>
      <LabeledInput
        label="Search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="buttermilk, eggs, brown sugar..."
      />

      <div style={{ display: 'grid', gap: 8 }}>
        {items.map((i) => (
          <div
            key={i.ingredient}
            style={{
              borderRadius: 12,
              border: '1px solid #eee',
              padding: '0.75rem 0.9rem',
              background: '#fafafa',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 8,
                alignItems: 'baseline',
              }}
            >
              <div style={{ fontWeight: 700 }}>{i.ingredient}</div>
              {i.ratio && (
                <div style={{ fontSize: '0.8rem', color: '#555' }}>Ratio: {i.ratio}</div>
              )}
            </div>
            {i.notes && (
              <div style={{ marginTop: 4, fontSize: '0.85rem', color: '#666' }}>{i.notes}</div>
            )}
            <div style={{ marginTop: 6, display: 'grid', gap: 4 }}>
              {i.subs.map((s, idx) => (
                <div key={idx} style={{ fontSize: '0.9rem' }}>
                  <strong>{s.title}:</strong>{' '}
                  <span style={{ fontWeight: 400 }}>{s.desc}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div style={{ fontSize: '0.85rem', color: '#777' }}>
            No matches. Try a simpler term (e.g., “butter”).
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------------- SMALL HELPERS ---------------------- */

function LabeledInput({ label, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: '0.8rem', color: '#666' }}>{label}</label>
      <input
        {...props}
        style={{
          padding: '0.45rem 0.6rem',
          borderRadius: 8,
          border: '1px solid #ddd',
          fontSize: '0.95rem',
        }}
      />
    </div>
  );
}

function UnitPicker({ label, value, options, onChange }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: '0.8rem', color: '#666' }}>{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          padding: '0.45rem 0.6rem',
          borderRadius: 8,
          border: '1px solid #ddd',
          fontSize: '0.95rem',
        }}
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );
}

function toInt(s) {
  const n = parseInt(String(s || '0'), 10);
  return Number.isNaN(n) ? 0 : n;
}

function createConverter() {
  const volumeToMl = {
    tsp: 4.92892,
    tbsp: 14.7868,
    fl_oz: 29.5735,
    cup: 236.588,
    pt: 473.176,
    qt: 946.353,
    gal: 3785.41,
    ml: 1,
    l: 1000,
  };

  const weightToG = {
    g: 1,
    kg: 1000,
    oz: 28.3495,
    lb: 453.592,
  };

  function roundSmart(n, fixed) {
    const d = fixed ?? (Math.abs(n) < 1 ? 3 : Math.abs(n) < 10 ? 2 : 1);
    return Number(n.toFixed(d));
  }

  function temp(from, to, v) {
    let c;
    if (from === 'C') c = v;
    else if (from === 'F') c = ((v - 32) * 5) / 9;
    else if (from === 'K') c = v - 273.15;
    else throw new Error('temp');
    if (to === 'C') return roundSmart(c, 2);
    if (to === 'F') return roundSmart((c * 9) / 5 + 32, 1);
    if (to === 'K') return roundSmart(c + 273.15, 2);
    throw new Error('temp');
  }

  return function convert(category, from, to, value) {
    if (category === 'temperature') return temp(from, to, value);
    if (category === 'volume') {
      const ml = value * volumeToMl[from];
      return roundSmart(ml / volumeToMl[to]);
    }
    if (category === 'weight') {
      const g = value * weightToG[from];
      return roundSmart(g / weightToG[to]);
    }
    throw new Error('unknown category');
  };
}

function substitutionsData() {
  return [
    {
      ingredient: 'Buttermilk',
      aliases: ['butter milk'],
      ratio: '1 cup',
      notes: 'Great for pancakes, cakes, fried chicken marinades.',
      subs: [
        {
          title: 'Milk + Acid',
          desc: '1 cup milk + 1 Tbsp lemon juice or white vinegar; rest 5–10 min.',
        },
        {
          title: 'Yogurt + Milk',
          desc: '3/4 cup plain yogurt + 1/4 cup milk; whisk until pourable.',
        },
      ],
    },
    {
      ingredient: 'Baking Powder',
      ratio: '1 tsp',
      notes: "Leavener; don’t over-sub or you’ll taste it.",
      subs: [
        {
          title: 'Baking Soda + Acid',
          desc: '1/4 tsp baking soda + 1/2 tsp cream of tartar.',
        },
        {
          title: 'Baking Soda + Buttermilk',
          desc: '1/4 tsp baking soda; replace 1/2 cup liquid with buttermilk and reduce other acid.',
        },
      ],
    },
    {
      ingredient: 'Eggs (1 large)',
      aliases: ['egg'],
      ratio: '1 egg',
      notes: 'Texture varies by recipe; not great for egg-forward dishes.',
      subs: [
        {
          title: 'Flax Egg',
          desc: '1 Tbsp ground flax + 3 Tbsp water; rest 5–10 min (best in quick breads).',
        },
        {
          title: 'Applesauce',
          desc: '1/4 cup unsweetened applesauce (adds moisture, less binding).',
        },
        {
          title: 'Yogurt',
          desc: '1/4 cup plain yogurt (moisture; tender crumb).',
        },
      ],
    },
    {
      ingredient: 'Brown Sugar',
      ratio: '1 cup',
      subs: [
        {
          title: 'White Sugar + Molasses',
          desc: '1 cup granulated sugar + 1 Tbsp molasses (light) or 2 Tbsp (dark).',
        },
        {
          title: 'Coconut Sugar',
          desc: '1:1 by volume; yields dryer, caramel notes.',
        },
      ],
    },
    {
      ingredient: 'Self-Rising Flour',
      ratio: '1 cup',
      subs: [
        {
          title: 'AP Flour + Leavening + Salt',
          desc: '1 cup all-purpose flour + 1 1/2 tsp baking powder + 1/4 tsp salt.',
        },
      ],
    },
  ];
}
